/**
 * Hyperliquid-style orderbook grouping (spread) steps by mid price.
 * Matches app.hyperliquid.xyz selectors, e.g.:
 *   BTC (~1e5) → 1, 2, 5, 10, 100, 1000
 *   ETH (~1e3) → 0.1, 0.2, 0.5, 1, 10, 100
 *   SOL (~1e2) → 0.01, 0.02, 0.05, 0.1, 1, 10
 */

const MULTS = [1, 2, 5, 10, 100, 1000];
const LS_PREFIX = 'omni.hlSpreadStep.';

function cleanStep(n) {
  if (!Number.isFinite(n) || n <= 0) return n;
  if (n >= 1) return Math.round(n * 1e8) / 1e8;
  return Number(n.toFixed(12).replace(/\.?0+$/, ''));
}

/** Price-tier unit used to build the 6 HL grouping buttons. */
export function getHlSpreadUnit(midPx) {
  const px = Number(midPx);
  if (!(px > 0) || !Number.isFinite(px)) return 0.01;
  if (px >= 10000) return 1;
  if (px >= 1000) return 0.1;
  if (px >= 100) return 0.01;
  if (px >= 10) return 0.001;
  if (px >= 1) return 0.0001;
  if (px >= 0.1) return 0.00001;
  if (px >= 0.01) return 0.000001;
  if (px >= 0.001) return 0.0000001;
  return 0.00000001;
}

/** Six grouping steps for the given mid (HL UI). */
export function getHlSpreadSteps(midPx) {
  const unit = getHlSpreadUnit(midPx);
  return MULTS.map((m) => cleanStep(m * unit));
}

export function spreadStepLabel(step) {
  const n = Number(step);
  if (!Number.isFinite(n)) return '—';
  if (n >= 1) return String(n);
  return n.toFixed(8).replace(/\.?0+$/, '');
}

export function stepsEqual(a, b) {
  return Math.abs(Number(a) - Number(b)) < 1e-12;
}

export function spreadStepStorageKey(dex, coin) {
  return `${LS_PREFIX}${(dex || 'main').toLowerCase()}:${String(coin || '').toUpperCase()}`;
}

/** Restore saved step if still valid for this asset's current tier; else finest. */
export function loadSavedSpreadStep(storageKey, steps) {
  const list = steps?.length ? steps : getHlSpreadSteps(null);
  try {
    const raw = localStorage.getItem(storageKey);
    if (raw == null || raw === '') return list[0];
    const n = Number(raw);
    const hit = list.find((s) => stepsEqual(s, n));
    return hit ?? list[0];
  } catch {
    return list[0];
  }
}

export function saveSpreadStep(storageKey, step) {
  try {
    localStorage.setItem(storageKey, String(step));
  } catch {
    /* ignore quota / private mode */
  }
}

/**
 * Aggregate L2 levels into HL-style price buckets.
 * Accepts either [[px, sz, n], ...] or [{ price, size }, ...].
 * Returns the same shape as the input levels.
 */
export function aggregateBookLevels(levels, step, side) {
  if (!step || step <= 0 || !levels?.length) return levels || [];
  const asTuple = Array.isArray(levels[0]);
  const buckets = new Map();

  for (const l of levels) {
    const p = Number(asTuple ? l[0] : l.price);
    const sz = Number(asTuple ? l[1] : l.size) || 0;
    const n = asTuple ? Number(l[2]) || 0 : Number(l.n) || 0;
    if (!Number.isFinite(p)) continue;
    const bucket =
      side === 'bid'
        ? Math.floor(p / step + 1e-9) * step
        : Math.ceil(p / step - 1e-9) * step;
    const price = cleanStep(bucket);
    const key = price.toFixed(12);
    const cur = buckets.get(key) || { price, size: 0, n: 0 };
    cur.size += sz;
    cur.n += n;
    buckets.set(key, cur);
  }

  const sorted = Array.from(buckets.values()).sort((a, b) =>
    side === 'bid' ? b.price - a.price : a.price - b.price
  );

  if (asTuple) return sorted.map((b) => [b.price, b.size, b.n || 1]);
  return sorted.map((b) => ({ price: b.price, size: b.size, n: b.n || undefined }));
}

/** Snap a price onto the bid or ask grouping grid. */
export function snapBookPrice(px, step, side) {
  const p = Number(px);
  const s = Number(step);
  if (!(p > 0) || !(s > 0)) return null;
  const bucket = side === 'bid' ? Math.floor(p / s + 1e-9) * s : Math.ceil(p / s - 1e-9) * s;
  return cleanStep(bucket);
}

/**
 * Always return `count` consecutive buckets from `startPx` so the ladder height
 * never changes when a level appears or disappears.
 */
export function fillBookWindow(aggregated, step, side, startPx, count) {
  const s = Number(step);
  const start = snapBookPrice(startPx, s, side);
  if (start == null || !(count > 0)) return [];
  const map = new Map();
  for (const l of aggregated || []) {
    const asTuple = Array.isArray(l);
    const p = Number(asTuple ? l[0] : l.price);
    if (!Number.isFinite(p)) continue;
    const snapped = snapBookPrice(p, s, side);
    if (snapped == null) continue;
    map.set(snapped.toFixed(12), [snapped, Number(asTuple ? l[1] : l.size) || 0, Number(asTuple ? l[2] : l.n) || 0]);
  }
  const out = [];
  let px = start;
  for (let i = 0; i < count; i += 1) {
    out.push(map.get(px.toFixed(12)) || [px, 0, 0]);
    px = cleanStep(side === 'ask' ? px + s : px - s);
  }
  return out;
}
